/**
 * Состояния запроса: загрузка и ошибка (docs/13-acceptance.md, этап 7).
 *
 * Правило одно на всё приложение: пока данных нет, экран говорит, чего ждёт,
 * а при ошибке — что случилось и что сделать. Пустой экран без объяснения
 * не показывается нигде.
 */
import { Button, Card, Icon } from '@/ds';
import { ApiError } from '@/api/client';

/** Строка ожидания. Скелетов нет: запросы локальные и отвечают быстро. */
export function Loading({ what }: { what: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}
    >
      {`Загружаем ${what}`}
    </div>
  );
}

/**
 * Человеческое объяснение ошибки. Технические подробности сюда не идут:
 * сервер уже прислал понятное сообщение, а сетевой сбой объясняется сам.
 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 0) return 'Сервер не отвечает. Проверьте, что он запущен';
    if (error.status === 401) return 'Сессия закончилась. Войдите заново';
    if (error.status >= 500) return 'Сервер ответил ошибкой. Попробуйте обновить через минуту';
    return error.message;
  }
  return 'Что-то пошло не так. Попробуйте обновить';
}

export function ErrorState({
  what,
  error,
  onRetry,
  retrying,
}: {
  /** Что не удалось загрузить: «кошельки», «сервис». */
  what: string;
  error: unknown;
  onRetry?: (() => void) | undefined;
  retrying?: boolean | undefined;
}) {
  return (
    <Card padding="lg" style={{ maxWidth: 620 }}>
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 'var(--radius-md)',
          background: 'var(--surface-brand-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="CloudOff" size={20} color="var(--burgundy-700)" />
      </span>

      <div
        style={{
          marginTop: 'var(--space-5)',
          font: '500 18px var(--font-display)',
          letterSpacing: 'var(--ls-heading)',
          color: 'var(--text-strong)',
        }}
      >
        {`Не удалось загрузить ${what}`}
      </div>

      <p
        style={{
          marginTop: 'var(--space-3)',
          font: '400 14px/1.6 var(--font-text)',
          color: 'var(--text-muted)',
          maxWidth: 460,
        }}
      >
        {errorMessage(error)}
      </p>

      {onRetry ? (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <Button
            variant="secondary"
            onClick={onRetry}
            loading={retrying ?? false}
            iconLeft={<Icon name="RefreshCw" size={16} />}
          >
            Попробовать снова
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Загрузка и ошибка одним вызовом. Возвращает разметку состояния либо
 * null, если данные пришли и экран можно рисовать дальше.
 */
export function QueryState({
  query,
  what,
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown; isFetching: boolean };
  what: string;
}) {
  if (query.isLoading) return <Loading what={what} />;
  if (query.isError) {
    return (
      <ErrorState
        what={what}
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  return null;
}
